import { useRef } from 'react';
import { useLabels, useSitemap } from 'ws-scripts/modules/common';

import { useDrawer } from '../context/DrawerContext';
import { useDrawerNavigation } from '../context/DrawerNavigationContext';
import { DEVICE_TYPE_MOBILE } from '../global-constants';
import useDeviceType from './useDeviceType';
import useMyCarsEnabled from './useMyCarsEnabled';
import useFragmentLoaded from './useFragmentLoaded';
import {
	MYCARS_HEADER_FRAGMENT_ALIAS,
	MYCARS_CONTENT_FRAGMENT_ALIAS,
	openMyCarsModal
} from '../utils/myCarsIntegration';

/** Stable id for the synthetic Level 2 drilldown item built below. */
const MYCARS_DRILLDOWN_ID = 'mycars-drilldown';

/**
 * useMyCarsCta — gating and click-handling for the drawer's My Cars CTA
 * button. Desktop and mobile behave differently on click:
 *   - Desktop dispatches ws-mycars-nav-open-modal and closes the drawer.
 *   - Mobile drills into a native Level 2 panel instead, reusing
 *     SubMenuPanel's own embedded-fragment rendering rather than a new panel.
 *
 * The desktop button is disabled until the header fragment finishes loading
 * (US2059853 T53). This hook doesn't own that fragment's fetch itself --
 * MyCarsHeaderIcon's headless mount does -- so it just resolves the same
 * MYCARS_HEADER_FRAGMENT_ALIAS sitemap alias and reads that fragment's status
 * via useFragmentLoaded. This closes a real race: ws-mycars-nav's modal
 * listener needs its own bundle to download and run before it can hear the
 * open-modal event, so a click before that happens would otherwise be
 * silently dropped.
 *
 * The mobile drilldown entry is unaffected by any of this: it opens the
 * separate content fragment inline, rather than depending on
 * ws-mycars-nav's OPEN_MODAL_EVENT listener, so it was never subject to that
 * race in the first place.
 *
 * @returns {{isEnabled: boolean|null, isDisabled: boolean, handleClick: () => void, buttonRef: import('react').RefObject}}
 */
const useMyCarsCta = () => {
	const labels = useLabels();
	const { isOpen, closeDrawer } = useDrawer();
	const { onSubmenuOpen } = useDrawerNavigation();
	const sitemap = useSitemap();
	const isMobile = useDeviceType() === DEVICE_TYPE_MOBILE;
	const buttonRef = useRef(null);
	const isEnabled = useMyCarsEnabled();
	const { isLoaded: isFragmentLoaded } = useFragmentLoaded(
		MYCARS_HEADER_FRAGMENT_ALIAS
	);
	// Only the desktop entry point depends on ws-mycars-nav's OPEN_MODAL_EVENT
	// listener having attached -- the mobile drilldown opens the separate
	// content fragment inline and isn't subject to that race.
	//
	// This same line also covers a site where My Cars is turned on but
	// misconfigured: the fragment then never finishes loading, so the button
	// just stays disabled forever instead of briefly then turning on. There's
	// no way to tell "still loading" apart from "never going to load," so we
	// don't try -- a permanently disabled button is the right outcome either way.
	const isDisabled = !isMobile && !isFragmentLoaded;

	const handleClick = () => {
		if (isMobile) {
			onSubmenuOpen(
				{
					id: MYCARS_DRILLDOWN_ID,
					label: labels.get('MY_CARS'),
					navigationItems: [
						{
							embedded: true,
							url: sitemap.get(MYCARS_CONTENT_FRAGMENT_ALIAS)
						}
					]
				},
				buttonRef
			);
			return;
		}

		openMyCarsModal(closeDrawer, isOpen);
	};

	return { isEnabled, isDisabled, handleClick, buttonRef };
};

export default useMyCarsCta;
